import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { Box, Button, Container, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { RouterLinks } from '@zeta-commons';
import { getHighestRankedSubscriptionPlanLabel, useSubscriptionDetailsStore, useSubscriptionPlansStore } from '@zeta-core';
import * as React from 'react';
import { useNavigate } from 'react-router';

export default function RestrictedAccess() {
  const navigate = useNavigate();
  const currentSubscription = useSubscriptionDetailsStore((state) => state.subscription);
  const subscriptionPlans = useSubscriptionPlansStore((state) => state.subscriptionPlans);
  const currentPlanName = getHighestRankedSubscriptionPlanLabel(currentSubscription, subscriptionPlans, 'No active plan');
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: (theme) => `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[100]} 100%)`,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            borderRadius: 4,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
          }}
        >
          {/* Back Button */}
          <Tooltip title="Go back" arrow placement="top">
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              aria-label="Go back"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Stack spacing={3} alignItems="center">
            {/* Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <LockOutlinedIcon fontSize="large" />
            </Box>

            {/* Title */}
            <Typography variant="h4" fontWeight={600}>
              Upgrade Required
            </Typography>

            {/* Message */}
            <Typography color="text.secondary">
              This feature isn’t included in your current subscription plan. Upgrade to unlock full access and continue without interruption.
            </Typography>

            {/* Plan hint */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'grey.100',
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Your current plan: <strong>{currentPlanName}</strong>
              </Typography>
            </Box>

            {/* CTA */}
            <Button
              size="large"
              variant="contained"
              startIcon={<UpgradeIcon />}
              onClick={() => navigate(RouterLinks.pricing)}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Upgrade Plan
            </Button>

            <Button
              variant="text"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'primary.main',
                },
              }}
            >
              Cancel
            </Button>

            {/* Secondary */}
            <Typography variant="caption" color="text.secondary">
              Need help choosing the right plan? Contact support anytime.
            </Typography>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
